import type { EditorContext, ViewportTarget } from './appState.js';
import type { AppState } from './appState.js';
import type { ComponentId, Scene, SceneNodeId } from '../scene/sceneState.js';

export function viewportTargetKey(target: ViewportTarget): string {
  switch (target.type) {
    case 'scene':
      return 'scene';
    case 'component':
      return `component:${target.componentId}`;
    case 'layer':
      return `layer:${target.componentId ?? 'scene'}:${target.layerId}`;
    case 'frame':
      return `frame:${target.componentId ?? 'scene'}:${target.frameId}`;
    case 'geometry-library':
    case 'material-library':
      return `${target.type}:${target.libraryItemId}`;
  }
}

export function getViewportTargetLabel(appState: AppState, target: ViewportTarget): string {
  switch (target.type) {
    case 'scene':
      return 'Scene';
    case 'component': {
      const component = appState.componentLibrary[target.componentId];
      return `Component: ${component?.name ?? target.componentId}`;
    }
    case 'layer': {
      const layer = appState.layerLibrary[target.layerId];
      return `Layer: ${layer?.name ?? target.layerId}`;
    }
    case 'frame': {
      const scene = getOwnerSceneFor(appState, target.componentId);
      const frame = scene?.nodes[target.frameId];
      const frameName = frame?.kind === 'frame' ? frame.name : target.frameId;
      return `Frame: ${frameName}`;
    }
    case 'geometry-library':
      return `Library: Geometry / ${libraryGeometryNameFor(appState, target.libraryItemId)}`;
    case 'material-library':
      return `Library: Material / ${libraryMaterialNameFor(appState, target.libraryItemId)}`;
  }
}

export function isViewportTargetValid(appState: AppState, target: ViewportTarget): boolean {
  switch (target.type) {
    case 'scene':
      return true;
    case 'component':
      return !!appState.componentLibrary[target.componentId];
    case 'layer': {
      const ownerScene = getOwnerSceneFor(appState, target.componentId);
      return !!appState.layerLibrary[target.layerId]
        && !!ownerScene
        && Object.values(ownerScene.nodes).some(node => node.kind === 'layer' && node.layerId === target.layerId);
    }
    case 'frame': {
      const scene = getOwnerSceneFor(appState, target.componentId);
      return scene?.nodes[target.frameId]?.kind === 'frame';
    }
    case 'geometry-library':
      return !!(appState.geometryLibrary.builtIn[target.libraryItemId] ?? appState.geometryLibrary.user[target.libraryItemId]);
    case 'material-library':
      return !!(appState.materialLibrary.builtIn[target.libraryItemId] ?? appState.materialLibrary.user[target.libraryItemId]);
  }
}

export function viewportTargetFromEditorContext(context: EditorContext): ViewportTarget | null {
  if (context.mode === 'geometry-library') return { type: 'geometry-library', libraryItemId: context.libraryItemId };
  if (context.mode === 'material-library') return { type: 'material-library', libraryItemId: context.libraryItemId };
  if (context.mode === 'layer' && context.layerId) {
    return {
      type: 'layer',
      componentId: context.componentId,
      layerId: context.layerId,
    };
  }
  return null;
}

export function viewportTargetFromSceneNode(
  appState: AppState,
  componentId: string | null,
  nodeId: SceneNodeId | null,
): ViewportTarget | null {
  if (!nodeId) return null;
  const scene = getOwnerSceneFor(appState, componentId ?? undefined);
  const node = scene?.nodes[nodeId];
  if (!node) return null;
  if (node.kind === 'frame') return { type: 'frame', componentId: componentId ?? undefined, frameId: node.id };
  if (node.kind === 'layer') {
    return {
      type: 'layer',
      componentId: componentId ?? undefined,
      layerId: node.layerId,
      sceneNodeId: node.id,
    };
  }
  return { type: 'component', componentId: node.componentId };
}

export function getOwnerSceneFor(appState: AppState, componentId?: ComponentId): Scene | null {
  if (!componentId) return appState.scene;
  return appState.componentLibrary[componentId]?.scene ?? null;
}

function libraryGeometryNameFor(appState: AppState, id: string): string {
  return (appState.geometryLibrary.builtIn[id] ?? appState.geometryLibrary.user[id])?.name ?? id;
}

function libraryMaterialNameFor(appState: AppState, id: string): string {
  return (appState.materialLibrary.builtIn[id] ?? appState.materialLibrary.user[id])?.name ?? id;
}
